import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { BlockData, TimestampVerification, AnomalyRecord, ApiHealthStatus, PredictionData } from '../types/bitcoin';
import { bitcoinApi } from '../services/bitcoinApi';
import { anomalyDetection } from '../services/anomalyDetection';
import { storage } from '../services/storage';
import { notifications } from '../services/notifications';

// State interface
interface BitcoinDataState {
  // Current block data
  latestBlock: BlockData | null;
  recentBlocks: BlockData[];
  
  // Verification and anomaly data
  currentVerification: TimestampVerification | null;
  recentAnomalies: AnomalyRecord[];
  
  // API status
  apiHealth: ApiHealthStatus;
  rateLimits: Record<string, number>;
  
  // Prediction data
  predictions: PredictionData | null;
  
  // UI state
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;
  lastUpdate: number;
}

// Action types
type BitcoinDataAction =
  | { type: 'SET_LATEST_BLOCK'; payload: BlockData }
  | { type: 'ADD_RECENT_BLOCK'; payload: BlockData }
  | { type: 'SET_VERIFICATION'; payload: TimestampVerification }
  | { type: 'ADD_ANOMALY'; payload: AnomalyRecord }
  | { type: 'UPDATE_API_HEALTH'; payload: ApiHealthStatus }
  | { type: 'UPDATE_RATE_LIMITS'; payload: Record<string, number> }
  | { type: 'SET_PREDICTIONS'; payload: PredictionData }
  | { type: 'SET_CONNECTION_STATUS'; payload: boolean }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'LOAD_RECENT_ANOMALIES'; payload: AnomalyRecord[] };

// Initial state
const initialState: BitcoinDataState = {
  latestBlock: null,
  recentBlocks: [],
  currentVerification: null,
  recentAnomalies: [],
  apiHealth: {} as ApiHealthStatus,
  rateLimits: {},
  predictions: null,
  isConnected: false,
  isLoading: false,
  error: null,
  lastUpdate: 0,
};

// Reducer
function bitcoinDataReducer(state: BitcoinDataState, action: BitcoinDataAction): BitcoinDataState {
  switch (action.type) {
    case 'SET_LATEST_BLOCK':
      return {
        ...state,
        latestBlock: action.payload,
        lastUpdate: Date.now(),
      };

    case 'ADD_RECENT_BLOCK':
      return {
        ...state,
        recentBlocks: [action.payload, ...state.recentBlocks].slice(0, 20), // Keep last 20 blocks
        lastUpdate: Date.now(),
      };

    case 'SET_VERIFICATION':
      return {
        ...state,
        currentVerification: action.payload,
        lastUpdate: Date.now(),
      };

    case 'ADD_ANOMALY':
      return {
        ...state,
        recentAnomalies: [action.payload, ...state.recentAnomalies].slice(0, 100), // Keep last 100 anomalies
        lastUpdate: Date.now(),
      };

    case 'UPDATE_API_HEALTH':
      return {
        ...state,
        apiHealth: action.payload,
        lastUpdate: Date.now(),
      };

    case 'UPDATE_RATE_LIMITS':
      return {
        ...state,
        rateLimits: action.payload,
        lastUpdate: Date.now(),
      };

    case 'SET_PREDICTIONS':
      return {
        ...state,
        predictions: action.payload,
        lastUpdate: Date.now(),
      };

    case 'SET_CONNECTION_STATUS':
      return {
        ...state,
        isConnected: action.payload,
      };

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };

    case 'LOAD_RECENT_ANOMALIES':
      return {
        ...state,
        recentAnomalies: action.payload,
      };

    default:
      return state;
  }
}

// Context
const BitcoinDataContext = createContext<{
  state: BitcoinDataState;
  dispatch: React.Dispatch<BitcoinDataAction>;
  actions: {
    verifyBlock: (height: number) => Promise<void>;
    loadRecentAnomalies: () => Promise<void>;
    refreshApiHealth: () => void;
    calculatePredictions: () => Promise<void>;
  };
} | null>(null);

// Provider component
export function BitcoinDataProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(bitcoinDataReducer, initialState);

  // Action handlers
  const verifyBlock = async (height: number) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      console.log(`🔍 Verifying block ${height}...`);
      const verification = await anomalyDetection.crossVerifyBlock(height);
      dispatch({ type: 'SET_VERIFICATION', payload: verification });

      // Save verification to storage
      await storage.saveVerification(verification);

      // Send appropriate notification
      if (verification.consensus) {
        notifications.sendConsensusNotification(height, Object.keys(verification.sources).length);
      } else {
        // Create and save anomaly record
        const anomalyRecord = anomalyDetection.createAnomalyRecord(verification);
        if (anomalyRecord) {
          await storage.saveAnomaly(anomalyRecord);
          dispatch({ type: 'ADD_ANOMALY', payload: anomalyRecord });
          notifications.sendAnomalyNotification(anomalyRecord);
        }
      }

      dispatch({ type: 'SET_ERROR', payload: null });
    } catch (error) {
      console.error('Block verification failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Verification failed';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      notifications.sendApiErrorNotification('Verification Service', errorMessage);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const loadRecentAnomalies = async () => {
    try {
      const anomalies = await storage.getRecentAnomalies(50);
      dispatch({ type: 'LOAD_RECENT_ANOMALIES', payload: anomalies });
    } catch (error) {
      console.error('Failed to load recent anomalies:', error);
    }
  };

  const refreshApiHealth = () => {
    const health = bitcoinApi.getHealthStatus();
    const rateLimits = bitcoinApi.getRateLimitStatus();
    dispatch({ type: 'UPDATE_API_HEALTH', payload: health });
    dispatch({ type: 'UPDATE_RATE_LIMITS', payload: rateLimits });
  };

  const calculatePredictions = async () => {
    try {
      // Get recent verifications for prediction calculation
      const recentVerifications = await storage.getVerificationsByHeightRange(
        Math.max(0, (state.latestBlock?.height || 0) - 100),
        state.latestBlock?.height || 0
      );

      if (recentVerifications.length === 0) return;

      // Calculate clean timestamps (without anomalies)
      const cleanTimestamps = recentVerifications
        .filter(v => v.consensus)
        .map(v => v.median)
        .sort((a, b) => a - b);

      if (cleanTimestamps.length < 10) return; // Need at least 10 data points

      // Calculate block time statistics
      const blockTimes: number[] = [];
      for (let i = 1; i < cleanTimestamps.length; i++) {
        blockTimes.push(cleanTimestamps[i] - cleanTimestamps[i - 1]);
      }

      const mean = blockTimes.reduce((sum, time) => sum + time, 0) / blockTimes.length;
      const sortedTimes = [...blockTimes].sort((a, b) => a - b);
      const median = sortedTimes[Math.floor(sortedTimes.length / 2)];
      
      // Standard deviation calculation
      const variance = blockTimes.reduce((sum, time) => sum + Math.pow(time - mean, 2), 0) / blockTimes.length;
      const std = Math.sqrt(variance);

      // Simple next block prediction (using exponential distribution)
      const lambda = 1 / mean; // Rate parameter
      const nextBlockEstimate = mean; // Expected value
      const confidenceInterval = 1.96 * std; // 95% confidence

      const predictions: PredictionData = {
        nextBlockTime: {
          estimate: nextBlockEstimate,
          confidence: Math.min(0.95, cleanTimestamps.length / 100), // Confidence based on data points
          range: [
            Math.max(0, nextBlockEstimate - confidenceInterval),
            nextBlockEstimate + confidenceInterval
          ],
        },
        blockTimeStats: {
          mean,
          median,
          std,
          cleanDataPoints: cleanTimestamps.length,
        },
        forecastAccuracy: {
          rmse: std, // Use standard deviation as RMSE approximation
          improvementFromCleaning: ((recentVerifications.length - cleanTimestamps.length) / recentVerifications.length) * 100,
        },
      };

      dispatch({ type: 'SET_PREDICTIONS', payload: predictions });
      
      // Send prediction notification if significant change
      if (state.predictions && Math.abs(predictions.nextBlockTime.estimate - state.predictions.nextBlockTime.estimate) > 60) {
        notifications.sendPredictionNotification(predictions.nextBlockTime.estimate, predictions.nextBlockTime.confidence);
      }
    } catch (error) {
      console.error('Prediction calculation failed:', error);
    }
  };

  // Track connection status changes
  useEffect(() => {
    notifications.sendConnectionNotification(state.isConnected);
  }, [state.isConnected]);

  // Event listeners for WebSocket updates
  useEffect(() => {
    const handleNewBlock = (event: CustomEvent) => {
      const blockData = event.detail;
      console.log('📦 New block received via WebSocket:', blockData.height);
      
      const block: BlockData = {
        height: blockData.height,
        hash: blockData.id,
        timestamp: blockData.timestamp,
        medianTimePast: blockData.mediantime,
        difficulty: blockData.difficulty,
        source: 'mempool',
      };

      dispatch({ type: 'SET_LATEST_BLOCK', payload: block });
      dispatch({ type: 'ADD_RECENT_BLOCK', payload: block });
      dispatch({ type: 'SET_CONNECTION_STATUS', payload: true });

      // Send new block notification
      notifications.sendNewBlockNotification(blockData.height);
      // Automatically verify new blocks
      verifyBlock(blockData.height);
    };

    const handleAnomalyDetected = (event: CustomEvent) => {
      const { record } = event.detail;
      dispatch({ type: 'ADD_ANOMALY', payload: record });
      console.log('🚨 Anomaly detected and added to state');
    };

    // Add event listeners
    window.addEventListener('newBlock', handleNewBlock as EventListener);
    window.addEventListener('anomalyDetected', handleAnomalyDetected as EventListener);

    // Initial data load
    loadRecentAnomalies();
    refreshApiHealth();

    // Set up periodic health checks
    const healthCheckInterval = setInterval(refreshApiHealth, 30000); // Every 30 seconds

    return () => {
      window.removeEventListener('newBlock', handleNewBlock as EventListener);
      window.removeEventListener('anomalyDetected', handleAnomalyDetected as EventListener);
      clearInterval(healthCheckInterval);
    };
  }, []);

  // Update predictions when new blocks arrive
  useEffect(() => {
    if (state.latestBlock) {
      calculatePredictions();
    }
  }, [state.latestBlock]);

  const actions = {
    verifyBlock,
    loadRecentAnomalies,
    refreshApiHealth,
    calculatePredictions,
  };

  return (
    <BitcoinDataContext.Provider value={{ state, dispatch, actions }}>
      {children}
    </BitcoinDataContext.Provider>
  );
}

// Custom hook
export function useBitcoinData() {
  const context = useContext(BitcoinDataContext);
  if (!context) {
    throw new Error('useBitcoinData must be used within a BitcoinDataProvider');
  }
  return context;
}

export default BitcoinDataContext;